import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadContentSpinner from '../../../components/Utils/LoadContentSpinner';
import { formatPrice } from '../../../components/Utils/UtilsPricesFormat';
import FormattedDate from '../../../components/Utils/FormattedDate';
import PaymentLinkStatusBadge from './PaymentLinkStatusBadge';
import { fetchPaymentLinks, getPaymentLinksStats } from './paymentLinksStore';
import SessionBadge from '../../../components/Dashboard/SessionBadge';

export default function PaymentLinksList() {
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [copiedId, setCopiedId] = useState(null);
    const [links, setLinks] = useState([]);
    const [stats, setStats] = useState(getPaymentLinksStats([]));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = 'Liens de paiement - Events billetterie';
        let cancelled = false;
        setLoading(true);
        setError('');
        fetchPaymentLinks()
            .then(({ links: nextLinks, stats: nextStats }) => {
                if (cancelled) return;
                setLinks(nextLinks);
                setStats(nextStats);
            })
            .catch(() => {
                if (cancelled) return;
                setLinks([]);
                setStats(getPaymentLinksStats([]));
                setError('Impossible de charger les liens de paiement.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return links.filter((link) => {
            if (statusFilter !== 'all' && link.status !== statusFilter) return false;
            if (!q) return true;
            return (
                (link.recipient_name || '').toLowerCase().includes(q) ||
                (link.event_title || '').toLowerCase().includes(q) ||
                (link.ticket_title || '').toLowerCase().includes(q) ||
                (link.note || '').toLowerCase().includes(q) ||
                (link.token || '').toLowerCase().includes(q)
            );
        });
    }, [links, query, statusFilter]);

    const handleCopy = async (link) => {
        try {
            await navigator.clipboard.writeText(link.public_url);
            setCopiedId(link.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            // ignore
        }
    };

    if (loading) {
        return <LoadContentSpinner />;
    }

    return (
        <div className="relative ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz overflow-visible">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 -top-4 h-40 -z-10 opacity-70"
                style={{
                    background:
                        'radial-gradient(ellipse 70% 80% at 0% 0%, rgba(37,99,235,0.08), transparent 60%)',
                }}
            />

            <div className="c2g1r cwnq4 cnlq0 c6r0l mb-6">
                <div className="c2rn6 cdiog min-w-0">
                    <h1 className="text-gray-800 dark:text-gray-100 font-bold c459m cbtcb">
                        Liens de paiement
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                        Créez un lien prérempli pour un acheteur VIP, un hôte ou une réservation privée.
                        L’acheteur paie directement, sans chercher l’événement.
                    </p>
                </div>
                <Link
                    to="/dashboard/communiquer/liens-paiement/nouveau"
                    className="btn bg-primary-600 hover:bg-primary-700 text-white cdj8c cg0jr ch8z9 cilvw cyn7a shrink-0"
                >
                    <svg className="cbm9w cbmv0 coqgc" width={16} height={16} viewBox="0 0 16 16">
                        <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                    </svg>
                    <span>Nouveau lien</span>
                </Link>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
                    {error}
                </div>
            )}

            <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {[
                    { label: 'Actifs', value: stats.active },
                    { label: 'Payés', value: stats.paid },
                    { label: 'Total liens', value: stats.total },
                    { label: 'Collecté', value: formatPrice(stats.collected) },
                ].map((kpi) => (
                    <div
                        key={kpi.label}
                        className="rounded-xl border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-800 px-4 py-3.5"
                    >
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-1">
                            {kpi.label}
                        </p>
                        <p className="text-xl font-bold tabular-nums text-gray-800 dark:text-gray-100">
                            {kpi.value}
                        </p>
                    </div>
                ))}
            </section>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher destinataire, événement, note…"
                    className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm"
                >
                    <option value="all">Tous les statuts</option>
                    <option value="active">Actifs</option>
                    <option value="paid">Payés</option>
                    <option value="expired">Expirés</option>
                    <option value="cancelled">Annulés</option>
                    <option value="exhausted">Épuisés</option>
                </select>
            </div>

            {filtered.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-800/60 px-6 py-14 text-center">
                    <p className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1">
                        {links.length === 0 ? 'Aucun lien pour le moment' : 'Aucun résultat'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-md mx-auto">
                        {links.length === 0
                            ? 'Générez votre premier lien de paiement pour un client prestige ou une réservation VIP.'
                            : 'Modifiez vos filtres pour afficher d’autres liens.'}
                    </p>
                    {links.length === 0 && (
                        <Link
                            to="/dashboard/communiquer/liens-paiement/nouveau"
                            className="inline-flex btn bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                        >
                            Créer un lien
                        </Link>
                    )}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-gray-500">
                            <tr>
                                <th className="px-4 py-3 font-medium">Destinataire</th>
                                <th className="px-4 py-3 font-medium">Événement / Billet</th>
                                <th className="px-4 py-3 font-medium">Montant</th>
                                <th className="px-4 py-3 font-medium">Statut</th>
                                <th className="px-4 py-3 font-medium">Créé</th>
                                <th className="px-4 py-3 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((link) => {
                                const amount =
                                    Number(link.total_amount) ||
                                    (Number(link.unit_price) || 0) * (Number(link.quantity) || 0);
                                return (
                                    <tr
                                        key={link.id}
                                        className="border-t border-gray-100 dark:border-gray-700/80 hover:bg-gray-50/80 dark:hover:bg-gray-900/30 transition-colors"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-800 dark:text-gray-100">
                                                {link.recipient_name || 'Sans destinataire'}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {link.recipient_phone || link.recipient_email || '—'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-gray-800 dark:text-gray-100 font-medium truncate max-w-[14rem]">
                                                {link.event_title || '—'}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {link.pricing_mode === 'custom' ? (
                                                    <>
                                                        Montant libre
                                                        {link.ticket_title ? ` · ${link.ticket_title}` : ''}
                                                    </>
                                                ) : (
                                                    <>
                                                        {link.ticket_title || '—'}
                                                        {link.quantity > 1 ? ` · ×${link.quantity}` : ''}
                                                    </>
                                                )}
                                                {link.session_display_label && (
                                                    <span className="block mt-1">
                                                        <SessionBadge label={link.session_display_label} />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                                            {formatPrice(amount)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <PaymentLinkStatusBadge status={link.status} />
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                            <FormattedDate date={link.created_at} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopy(link)}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                                    title="Copier le lien"
                                                    aria-label="Copier le lien"
                                                >
                                                    {copiedId === link.id ? (
                                                        <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                        </svg>
                                                    )}
                                                </button>
                                                <Link
                                                    to={`/dashboard/communiquer/liens-paiement/${link.id}`}
                                                    className="text-xs font-semibold text-primary-700 dark:text-primary-300 hover:underline px-2"
                                                >
                                                    Détail
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
